// built-from: /triage-flow v1 impl:triage-flow role:implementation
// The Where-you-are panel (was the Starting point panel, was the triage
// banner). 056-vision-capture-method B2 (spec §6 output oracle "triage
// banner with tier + trigger evidence"; approved addition #2: the tier is a
// CONFIRMABLE FACT, never a silent branch), RE-HOUSED by the owner-approved
// redesign, 2026-07-09 (specs/056-vision-capture-method/redesign-brief.md),
// and RE-MAPPED by the interview design (owner-agreed interview design,
// 2026-07-09; specs/056-vision-capture-method/interview-design.md): the
// panel is titled "Where you are" (the first plain stage name) and carries
// the vision.participant card (who we are speaking with) AHEAD of the tier
// content, lifecycle-tagged and confirmable like any fact. Once the tier
// confirms the panel collapses to the presentational strip; an unconfirmed
// participant stays confirmable above it (a fact on the surface never
// silently disappears), and a confirmed one folds into the strip line.
//
// EVOLVED COPY (agreed session-opening design, owner 2026-07-08, kept): the
// visible panel speaks plain English only. The tier value selects an agreed
// sentence (TIER_COPY below), the evidence line opens with "Why:", and no
// method jargon (tier, triage, or the tier names as labels) is ever
// rendered. The capability ids are unchanged.

import React, { useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import type { GatedFact, SurfaceEdit } from '../../facilitation/surfaceBinding';
import {
  Composer,
  DisputeComposer,
  DisputedBlock,
  FactCard,
  InPlaceField,
  LifecycleTag,
  type EmitResult,
} from './factCardKit';

// The proportionality tiers (mirrors the server vocabulary in
// functions/_factEvents.js TIERS: bundle data, not engine).
const TIERS = ['tactical', 'stream', 'orientation'] as const;

// The agreed plain-English banner copy, VERBATIM per tier value (agreed
// session-opening design, owner 2026-07-08; alongside specs/
// 056-vision-capture-method). The tier value stays a protocol key on the
// capability seam; the user only ever reads these sentences. No method
// jargon reaches the screen.
const TIER_COPY: Record<string, string> = {
  tactical:
    'This looks like a contained problem we can fix directly, rather than a strategy question.',
  stream:
    'This touches several teams, so it needs shared agreement on the approach before fixes.',
  orientation: 'This needs a proper diagnosis before we commit to fixes.',
};

type TriggerLike = Record<string, unknown>;

function triggerText(trigger: unknown): string {
  if (!trigger || typeof trigger !== 'object') return '';
  const t = trigger as TriggerLike;
  for (const key of ['description', 'evidence', 'text', 'id']) {
    if (typeof t[key] === 'string' && (t[key] as string).length > 0) return t[key] as string;
  }
  return '';
}

function participantLine(participant: GatedFact): string {
  if (typeof participant.content.text === 'string' && participant.content.text.trim()) {
    return participant.content.text;
  }
  const name = typeof participant.content.name === 'string' ? participant.content.name : '';
  const role =
    typeof participant.content.role === 'string' && participant.content.role.length > 0
      ? participant.content.role
      : null;
  return role ? `${name}, ${role}` : name;
}

// The quiet one-line participant card (owner-agreed interview design,
// 2026-07-09): the card grammar's lifecycle tag and one-click Confirm on
// the vision.participant singleton, nothing louder.
const ParticipantCard: React.FC<{
  participant: GatedFact;
  sealed: boolean;
  frozen: boolean;
  pulsing: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}> = ({ participant, sealed, frozen, pulsing, registerRef, onEmitEdit }) => {
  const busy = participant.provisionalLocal === true;
  const confirmable =
    participant.lifecycle !== 'confirmed' && participant.lifecycle !== 'disputed';
  const className = [
    'vsf-card',
    `vsf-card--${participant.lifecycle}`,
    'vsf-participant',
    pulsing ? 'vsf-pulse' : '',
    sealed ? 'vsf-card--sealed' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={className}
      data-fact-id="vision.participant"
      tabIndex={-1}
      ref={(el) => registerRef('vision.participant', el)}
    >
      <span className="vsf-mut">Speaking with</span>
      {participant.lifecycle === 'disputed' ? (
        <DisputedBlock
          fact={participant}
          noun="participant"
          disabled={busy || frozen}
          onEmitEdit={onEmitEdit}
        />
      ) : sealed ? (
        <span className="vsf-static-text">{participantLine(participant)}</span>
      ) : (
        <InPlaceField
          fact={{
            ...participant,
            content: { ...participant.content, text: participantLine(participant) },
          }}
          noun="participant"
          disabled={busy || frozen}
          onEmitEdit={onEmitEdit}
        />
      )}
      <div className="vsf-card-foot">
        <LifecycleTag fact={participant} />
        {!sealed && confirmable && (
          <button
            type="button"
            className="vsf-btn vsf-confirm"
            disabled={busy || frozen}
            onClick={() =>
              onEmitEdit({
                capabilityId: 'vision.confirm_fact',
                input: { factId: 'vision.participant' },
              })
            }
          >
            <Check size={12} aria-hidden="true" /> Confirm
          </button>
        )}
        {!sealed && participant.lifecycle !== 'disputed' && (
          <DisputeComposer
            fact={participant}
            noun="participant"
            disabled={busy || frozen}
            onEmitEdit={onEmitEdit}
          />
        )}
        {!sealed && (
          <button
            type="button"
            className="vsf-reject"
            aria-label="Reject participant"
            disabled={busy || frozen}
            onClick={() => onEmitEdit({
              capabilityId: 'vision.reject_fact',
              input: { factId: participant.factId },
            })}
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export interface TriageBannerProps {
  tier?: GatedFact;
  participant?: GatedFact;
  drivers?: GatedFact[];
  active?: boolean;
  sealed: boolean;
  busy: boolean;
  pulsing: boolean;
  participantPulsing?: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}

const TriageBanner: React.FC<TriageBannerProps> = ({
  tier,
  participant,
  drivers = [],
  active = false,
  sealed,
  busy,
  pulsing,
  participantPulsing = false,
  registerRef,
  onEmitEdit,
}) => {
  const [adjusting, setAdjusting] = useState(false);
  const pendingLocal = tier?.provisionalLocal === true;
  const frozen = busy || pendingLocal;
  const tierName = tier && typeof tier.content.tier === 'string' ? tier.content.tier : '';
  const triggers = tier && Array.isArray(tier.content.triggers) ? tier.content.triggers : [];
  const triggerLine = triggers.map(triggerText).filter(Boolean).join(' · ');

  const participantCard = participant ? (
    <ParticipantCard
      participant={participant}
      sealed={sealed}
      frozen={busy}
      pulsing={participantPulsing}
      registerRef={registerRef}
      onEmitEdit={onEmitEdit}
    />
  ) : null;

  const driversBlock = drivers.length > 0 || (active && !sealed) ? (
    <div className="vsf-context-drivers">
      <h3 className="vsf-lane-head">
        Why this matters now <span className="vsf-link">pressure and consequence</span>
      </h3>
      <div className="vsf-lane-body">
        {drivers.map((driver) => (
          <FactCard
            key={driver.factId}
            fact={driver}
            noun="driver"
            sealed={sealed}
            frozen={busy}
            pulsing={false}
            registerRef={registerRef}
            onEmitEdit={onEmitEdit}
          />
        ))}
        {!sealed && active && (
          <Composer
            label="Add why change now"
            placeholder="e.g. Renewal delays are now driving customer loss"
            disabled={busy}
            onCommit={(text) =>
              onEmitEdit({ capabilityId: 'vision.add_driver', input: { text } })
            }
          />
        )}
      </div>
    </div>
  ) : null;

  // Confirmed tier: the presentational Where-you-are strip (owner-approved
  // redesign, 2026-07-09; label per the owner-agreed interview design,
  // 2026-07-09). No affordance rides the strip itself; the highlight ref
  // and the pulse cue keep working against it. A confirmed participant
  // folds into the strip line; an unconfirmed one keeps its card above it.
  if (tier && tier.lifecycle === 'confirmed') {
    const participantConfirmed = participant?.lifecycle === 'confirmed';
    return (
      <section
        className="vpanel vpanel--collapsed"
        aria-labelledby="vsf-starting-title"
        data-testid="vsf-starting-strip"
      >
        {!participantConfirmed && participantCard}
        {driversBlock}
        <div
          className={pulsing ? 'vpanel-strip vsf-pulse' : 'vpanel-strip'}
          data-fact-id="vision.tier"
          tabIndex={-1}
          ref={(el) => registerRef('vision.tier', el)}
        >
          <CheckCircle2 size={16} className="vpanel-strip-tick" aria-hidden="true" />
          <span id="vsf-starting-title" className="vpanel-strip-label">
            Where you are
          </span>
          {participant && participantConfirmed && (
            <>
              <span className="vpanel-strip-dot" aria-hidden="true">
                ·
              </span>
              <span className="vpanel-strip-summary">
                Speaking with {participantLine(participant)}
              </span>
            </>
          )}
          <span className="vpanel-strip-dot" aria-hidden="true">
            ·
          </span>
          <span className="vpanel-strip-summary">{TIER_COPY[tierName] ?? ''}</span>
        </div>
      </section>
    );
  }

  // Unconfirmed (or not yet proposed) tier: the expanded Where-you-are
  // panel, the active act's home. The participant card precedes the tier
  // content (owner-agreed interview design, 2026-07-09).
  return (
    <section
      className="vpanel vpanel--active"
      aria-labelledby="vsf-triage-title"
      data-testid="vsf-triage"
    >
      <div className="vpanel-head vpanel-head--active">
        <h2 id="vsf-triage-title" className="vpanel-title">
          Where you are
        </h2>
        {tier
          && (pendingLocal ? (
            <span className="vsf-tag vsf-tag--saving">
              <Loader2 size={12} className="vsf-spin" aria-hidden="true" /> Saving…
            </span>
          ) : (
            <span className="vsf-tag vsf-tag--open">
              <AlertTriangle size={12} aria-hidden="true" /> Awaiting confirm
            </span>
          ))}
      </div>

      {participantCard}
      {driversBlock}

      {tier && (
        <div
          className={pulsing ? 'vpanel-body vsf-pulse' : 'vpanel-body'}
          data-fact-id="vision.tier"
          tabIndex={-1}
          ref={(el) => registerRef('vision.tier', el)}
        >
          <div className="vsf-triage-row">
            <strong className="vsf-triage-title">{TIER_COPY[tierName] ?? ''}</strong>
            {!sealed && (
              <>
                <button
                  type="button"
                  className="vpanel-primary"
                  disabled={frozen}
                  onClick={() =>
                    onEmitEdit({ capabilityId: 'vision.confirm_tier', input: {} })
                  }
                >
                  <Check size={13} aria-hidden="true" /> Confirm
                </button>
                <button
                  type="button"
                  className="vsf-btn"
                  aria-expanded={adjusting}
                  disabled={frozen}
                  onClick={() => setAdjusting((open) => !open)}
                >
                  Adjust
                </button>
              </>
            )}
          </div>

          {/* The supporting evidence stays visible (the decision is auditable);
              the triggers' plain descriptions render behind "Why:". */}
          {triggerLine && <div className="vsf-mut">Why: {triggerLine}</div>}

          {adjusting && !sealed && (
            <div className="vsf-triage-adjust">
              {TIERS.filter((t) => t !== tierName).map((t) => (
                <button
                  key={t}
                  type="button"
                  className="vsf-btn"
                  disabled={frozen}
                  onClick={() => {
                    setAdjusting(false);
                    onEmitEdit({
                      capabilityId: 'vision.propose_tier',
                      input: { tier: t, triggers },
                    });
                  }}
                >
                  {TIER_COPY[t]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default TriageBanner;
